(function () {
  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isScrollableElement(element) {
    if (!element || element === document.body || element === document.documentElement) return false;

    var style = window.getComputedStyle(element);
    var canScroll = style.overflowY === 'auto' || style.overflowY === 'scroll';

    return canScroll && element.scrollHeight > element.clientHeight + 1;
  }

  function getScrollContext(root) {
    var current = root.parentElement;

    while (current && current !== document.body && current !== document.documentElement) {
      if (isScrollableElement(current)) {
        return { element: current, listenerTarget: current, isWindow: false };
      }

      current = current.parentElement;
    }

    return {
      element: document.scrollingElement || document.documentElement,
      listenerTarget: window,
      isWindow: true
    };
  }

  if (customElements.get('course-practice-section-nav')) return;

  class CoursePracticeSectionNav extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.guide = null;
      this.scrollContext = null;
      this.abortController = null;
      this.scrollFrame = null;
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleHashChange = this.handleHashChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      if (this.abortController) return;

      this.guide = this.closest('.myron-practice-guide') || this;
      var links = Array.prototype.slice.call(
        this.querySelectorAll('.myron-course-section-nav__link')
      );

      this.items = links.map((link) => ({ link: link, target: this.getTarget(link.getAttribute('href')) }))
        .filter((item) => item.target);

      if (!this.items.length || this.items.length !== links.length) return;

      this.dataset.enhanced = 'true';
      this.abortController = new AbortController();

      this.addEventListener('click', this.handleClick, { signal: this.abortController.signal });
      window.addEventListener('resize', this.handleResize, { passive: true, signal: this.abortController.signal });
      window.addEventListener('hashchange', this.handleHashChange, { signal: this.abortController.signal });
      window.addEventListener('popstate', this.handleHashChange, { signal: this.abortController.signal });

      this.attachScrollContext();
      this.activateHash(true);
      this.queueSync();
    }

    disconnectedCallback() {
      if (this.scrollContext) {
        this.scrollContext.listenerTarget.removeEventListener('scroll', this.handleScroll);
      }

      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.scrollContext = null;
      this.abortController = null;
      this.scrollFrame = null;
    }

    getTarget(hash) {
      if (!hash || hash.charAt(0) !== '#') return null;

      var target = document.getElementById(hash.slice(1));
      return target && this.guide && this.guide.contains(target) ? target : null;
    }

    getItemForHash(hash) {
      var target = this.getTarget(hash);
      return target ? this.items.find((item) => item.target === target) : null;
    }

    attachScrollContext() {
      var nextContext = getScrollContext(this.guide);

      if (this.scrollContext && this.scrollContext.listenerTarget === nextContext.listenerTarget) {
        this.scrollContext = nextContext;
        return nextContext;
      }

      if (this.scrollContext) {
        this.scrollContext.listenerTarget.removeEventListener('scroll', this.handleScroll);
      }

      this.scrollContext = nextContext;
      this.scrollContext.listenerTarget.addEventListener('scroll', this.handleScroll, { passive: true });

      return nextContext;
    }

    getViewport() {
      var context = this.scrollContext || this.attachScrollContext();
      var top = context.isWindow ? 0 : context.element.getBoundingClientRect().top;

      return {
        top: top,
        height: context.isWindow ? window.innerHeight : context.element.clientHeight
      };
    }

    getTopOffset() {
      var rootStyle = window.getComputedStyle(document.documentElement);
      var headerHeight = parseFloat(rootStyle.getPropertyValue('--header-height')) || 0;
      var navHeight = window.getComputedStyle(this).position === 'sticky'
        ? this.getBoundingClientRect().height
        : 0;

      return headerHeight + navHeight;
    }

    getActivationLine() {
      var viewport = this.getViewport();
      var availableTop = viewport.top + Math.min(this.getTopOffset(), Math.max(viewport.height - 24, 0));
      var availableHeight = Math.max(viewport.top + viewport.height - availableTop, 0);

      return availableTop + (availableHeight * 0.22);
    }

    isAtScrollBottom() {
      var context = this.scrollContext || this.attachScrollContext();
      var scrollTop = context.isWindow
        ? window.pageYOffset || context.element.scrollTop || 0
        : context.element.scrollTop;
      var viewportHeight = context.isWindow ? window.innerHeight : context.element.clientHeight;

      return scrollTop + viewportHeight >= context.element.scrollHeight - 3;
    }

    isWithinGuide() {
      var firstTarget = this.items[0].target;
      var lastTarget = this.items[this.items.length - 1].target;
      var viewport = this.getViewport();
      var viewportBottom = viewport.top + viewport.height;
      var firstRect = firstTarget.getBoundingClientRect();
      var lastRect = lastTarget.getBoundingClientRect();

      return firstRect.top < viewportBottom && lastRect.bottom > viewport.top;
    }

    getActiveItem() {
      if (this.isAtScrollBottom()) return this.items[this.items.length - 1];

      var activationLine = this.getActivationLine();
      var activeItem = this.items[0];

      this.items.forEach((item) => {
        if (item.target.getBoundingClientRect().top <= activationLine) {
          activeItem = item;
        }
      });

      return activeItem;
    }

    setActiveItem(activeItem, keepVisible) {
      if (!activeItem) return;

      var changed = false;

      this.items.forEach((item) => {
        var isActive = item === activeItem;

        if (isActive) {
          changed = changed || item.link.getAttribute('aria-current') !== 'location';
          item.link.setAttribute('aria-current', 'location');
        } else {
          item.link.removeAttribute('aria-current');
        }
      });

      if (keepVisible || changed) {
        this.scrollActiveLinkIntoView(activeItem.link);
      }
    }

    scrollActiveLinkIntoView(link) {
      var scroller = this.querySelector('.myron-practice-guide-nav__scroller');

      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

      var linkRect = link.getBoundingClientRect();
      var scrollerRect = scroller.getBoundingClientRect();
      var desiredLeft = scroller.scrollLeft
        + linkRect.left
        - scrollerRect.left
        - ((scroller.clientWidth - linkRect.width) / 2);
      var maxScrollLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
      var nextLeft = Math.min(Math.max(desiredLeft, 0), maxScrollLeft);

      if (typeof scroller.scrollTo === 'function') {
        scroller.scrollTo({
          left: nextLeft,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      } else {
        scroller.scrollLeft = nextLeft;
      }
    }

    scrollToTarget(target, behavior) {
      target.scrollIntoView({
        behavior: behavior || (prefersReducedMotion() ? 'auto' : 'smooth'),
        block: 'start'
      });
    }

    activateHash(shouldScroll) {
      var item = this.getItemForHash(window.location.hash);

      if (!item) {
        this.queueSync();
        return;
      }

      if (shouldScroll) {
        this.scrollToTarget(item.target, 'auto');
      }

      this.setActiveItem(item, true);
    }

    handleClick(event) {
      var link = event.target && event.target.closest
        ? event.target.closest('.myron-course-section-nav__link')
        : null;

      if (!link || !this.contains(link)) return;

      var item = this.items.find((candidate) => candidate.link === link);
      if (!item) return;

      event.preventDefault();
      this.scrollToTarget(item.target);

      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', link.getAttribute('href'));
      } else {
        window.location.hash = link.getAttribute('href');
      }

      this.setActiveItem(item, true);
      this.queueSync();
    }

    handleScroll() {
      this.queueSync();
    }

    handleResize() {
      this.attachScrollContext();
      this.queueSync();
    }

    handleHashChange() {
      this.attachScrollContext();
      this.activateHash(true);
    }

    queueSync() {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;

        var visible = this.isWithinGuide();
        this.dataset.visible = visible ? 'true' : 'false';

        if (visible) {
          this.setActiveItem(this.getActiveItem(), false);
        }
      });
    }
  }

  customElements.define('course-practice-section-nav', CoursePracticeSectionNav);
}());
